import { UseFormReturn } from 'react-hook-form';
import { SurveyFormData } from '@/utils/validation';
import RatingInput from '../Inputs/RatingInput';

interface SatisfactionRatingsSectionProps {
  form: UseFormReturn<SurveyFormData>;
}

const SatisfactionRatingsSection = ({
  form,
}: SatisfactionRatingsSectionProps) => {
  const {
    formState: { errors },
  } = form;

  const ratingFields = [
    {
      category: 'Product/Service Quality',
      fields: [
        {
          name: 'productQuality_expectationsMet' as const,
          label: 'Expectations met',
        },
        {
          name: 'productQuality_performanceAsExpected' as const,
          label: 'Delivered in good condition/performed as expected',
        },
      ],
    },
    {
      category: 'Delivery/Timeliness',
      fields: [
        {
          name: 'delivery_timeliness' as const,
          label: 'Completed within agreed timeframe',
        },
        {
          name: 'delivery_processEfficient' as const,
          label: 'Process efficient/coordinated',
        },
      ],
    },
    {
      category: 'Communication & Responsiveness',
      fields: [
        {
          name: 'communication_clearTimely' as const,
          label: 'Clear/timely',
        },
        {
          name: 'communication_promptResponses' as const,
          label: 'Prompt/satisfactory responses',
        },
      ],
    },
    {
      category: 'Staff Professionalism',
      fields: [
        {
          name: 'staff_professionalCourteousHelpful' as const,
          label: 'Professional/courteous/helpful',
        },
        {
          name: 'staff_supportAdequateEffective' as const,
          label: 'Adequate/effective support',
        },
      ],
    },
    {
      category: 'Price/Value for Money',
      fields: [
        {
          name: 'price_valueForMoney' as const,
          label: 'Pricing fair for value received',
        },
      ],
    },
    {
      category: 'Overall Satisfaction',
      fields: [
        {
          name: 'overall_overallSatisfied' as const,
          label: 'Overall satisfied',
        },
        {
          name: 'overall_wouldRecommend' as const,
          label: 'Would recommend company',
        },
      ],
    },
  ];

  return (
    <section aria-labelledby="section-b-heading" className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
      <div className="flex items-center mb-6">
        <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
          <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </div>
        <div>
          <h2
            id="section-b-heading"
            className="text-2xl font-bold text-gray-900"
          >
            Section B: Customer Satisfaction Rating
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Rate each item on a scale of 1 to 5
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {ratingFields.map((category, index) => (
          <div
            key={category.category}
            className={`${index !== ratingFields.length - 1 ? 'border-b border-gray-200 pb-6' : ''}`}
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <span className="w-2 h-2 bg-primary-500 rounded-full mr-2"></span>
              {category.category}
            </h3>
            <div className="space-y-4">
              {category.fields.map((field) => {
                const fieldError = errors.ratings?.[field.name]?.message as string | undefined;
                return (
                  <div key={field.name}>
                    <RatingInput
                      form={form}
                      name={field.name}
                      label={field.label}
                      {...(fieldError ? { error: fieldError } : {})}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default SatisfactionRatingsSection;

